import { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";

import { API } from '../../config/api';

const TeamModal = ({ team, countries, defaultCountry, onClose, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: team?.name || "",
    region: team?.region || "",
    country: team?.country || defaultCountry || "Italia",
    description: team?.description || "",
    is_external: team?.is_external || false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (team) {
        await axios.put(`${API}/teams/${team.id}`, formData);
        toast.success(t('teams.messages.updated'));
      } else {
        await axios.post(`${API}/teams`, formData);
        toast.success(t('teams.messages.created'));
      }
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {team ? t('teams.editTeam') : t('teams.newTeam')}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('teams.country')} *</label>
            <select
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
              required
              data-testid="team-country"
            >
              {countries.map((country) => (
                <option key={country} value={country}>
                  {t(`teams.countries.${country}`) || country}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('teams.name')} *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
              placeholder="es. Installatori Lombardia"
              required
              data-testid="team-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('teams.region')} *</label>
            <input
              type="text"
              value={formData.region}
              onChange={(e) => setFormData({ ...formData, region: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
              placeholder="es. Lombardia"
              required
              data-testid="team-region"
            />
          </div>
          
          {/* Team Type - Internal/External */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('teams.teamType')} *</label>
            <div className="flex gap-4">
              <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition ${
                !formData.is_external 
                  ? 'border-blue-500 bg-blue-50 text-blue-700' 
                  : 'border-gray-300 hover:bg-gray-50'
              }`}>
                <input
                  type="radio"
                  name="teamType"
                  checked={!formData.is_external}
                  onChange={() => setFormData({ ...formData, is_external: false })}
                  className="sr-only"
                />
                <span className="text-lg">🏠</span>
                <span className="font-medium">{t('teams.internal')}</span>
              </label>
              <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition ${
                formData.is_external 
                  ? 'border-orange-500 bg-orange-50 text-orange-700' 
                  : 'border-gray-300 hover:bg-gray-50'
              }`}>
                <input
                  type="radio"
                  name="teamType"
                  checked={formData.is_external}
                  onChange={() => setFormData({ ...formData, is_external: true })}
                  className="sr-only"
                />
                <span className="text-lg">🌐</span>
                <span className="font-medium">{t('teams.external')}</span>
              </label>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('teams.description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
              rows={3}
              data-testid="team-description"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-lojack-red text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              data-testid="team-save-btn"
            >
              {loading ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamModal;
